// Landscape Log timestamps must read the same for every viewer regardless of
// their machine's timezone (AUT-236) -- pin display to US Eastern and label
// the zone so the reader isn't misled into thinking it's their own local time.
// Assembled from formatToParts (rather than a plain Intl.DateTimeFormat
// string) so the punctuation matches the pre-existing "MMM d, yyyy h:mm a"
// shape exactly -- a naive format() call inserts a comma before the time.
export function formatEasternDateTime(isoDateString: string): string {
  const date = new Date(isoDateString);
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    }).formatToParts(date);

    const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";

    const month = get("month");
    const day = get("day");
    const year = get("year");
    const hour = get("hour");
    const minute = get("minute");
    const dayPeriod = get("dayPeriod");
    const timeZoneName = get("timeZoneName");

    return `${month} ${day}, ${year} ${hour}:${minute} ${dayPeriod} ${timeZoneName}`;
  } catch {
    return isoDateString;
  }
}

export function formatCompactRelativeTime(isoDateString: string): string {
  const diff = Date.now() - new Date(isoDateString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${Math.max(1, minutes)}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function formatRelativeTime(isoDateString: string | null): string {
  if (isoDateString === null) return 'Never';

  const diffMs = Date.now() - new Date(isoDateString).getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffMinutes < 1) return 'Just now';
  if (diffHours < 1) return `${diffMinutes} min ago`;
  if (diffDays < 1) return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  if (diffMonths < 1) return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
  return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
}
